import * as vp from 'vopay';
import { SideDrawer, Box, Card, BodyText } from 'vopay';
import { FunctionComponent, useState, useEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';
import { read, utils } from 'xlsx';
import { format, isBefore, parseISO } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { UtilButton, Button, Title } from '@/components/UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import ConvertTemplateUpgrade from './ConvertTemplateUpgrade/ConvertTemplateUpgrade';
import usePostBulkBulkPayout from '@/hooks/useApi/usePostBulkBulkPayout';
import usePostBulkMoneyRequest from '@/hooks/useApi/usePostBulkMoneyRequest';
import usePostBulkCollection from '@/hooks/useApi/usePostBulkCollection';
import usePostBulkPayment from '@/hooks/useApi/usePostBulkPayment';
import usePostBulkRTRCollection from '@/hooks/useApi/usePostBulkRTRCollection';
import usePostBulkRTRPayment from '@/hooks/useApi/usePostBulkRTRPayment';
import usePostBulkPaylink from '@/hooks/useApi/usePostBulkPaylink';
import usePostBulkPushToCardPayment from '@/hooks/useApi/usePostBulkPushToCardPayment';
import usePostBulkCreditCardCollection from '@/hooks/useApi/usePostBulkCreditCardCollection';
import usePostBulkACHCollection from '@/hooks/useApi/usePostBulkACHCollection';
import usePostBulkACHPayment from '@/hooks/useApi/usePostBulkACHPayment';
import usePostBulkRTP from '@/hooks/useApi/usePostBulkRTP';
import useBulkFedwireCollection from '@/hooks/useApi/useBulkFedwireCollection';
import useBulkFedwirePayment from '@/hooks/useApi/useBulkFedwirePayment';
import usePostBulkClientAccountTransfer from '@/hooks/useApi/usePostBulkClientAccountTransfer';
import usePostAccountBalance from '@/hooks/useApi/usePostAccountBalance';

import useFileConversion from '@/hooks/useApi/useFileConversion';
import FileUploadFormDisplay from '@/components/Upload/FileUpload/FIleUploadForm/FileUploadForm';
import FileUploadReview from '@/components/Upload/FileUpload/FileUploadReview/FileUploadReview';
import { FileUploadForm } from '@/components/Upload/FileUpload/types';
import { useClientData } from '@/providers/ClientData.provider';
import {
  processFile,
  parseCSV,
  processBankingFileStandard005,
  processBankingFileBMO80,
  processMoneyWay,
  parseCountry,
  parseProvince,
} from '@/components/Upload/FileUpload/utils/fileProcessingUtils';
import RadioCard from './RadioCard';
import ConvertMyTemplateFormValues from '../../ConvertMyTemplateForm/ConvertMyTemplateForm';
import { useDataFetchIndicator } from '@/hooks';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';

type FileUploadDrawerProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: (id?: string) => void;
};

type FC = FunctionComponent<FileUploadDrawerProps>;
const FileUploadDrawer: FC = ({ isOpen, onClose }) => {
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.FileUploadDrawer' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { t: tDialogue } = useTranslation('translations', { keyPrefix: 'FileUpload.Dialogs' });
  const [showUpgradePlanModal, setShowUpgradePlanModal] = useState(false);
  const [isReview, setIsReview] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const alertDialog = useAlertDialog();
  const history = useHistory();
  const [processingOption, setProcessingOption] = useState<'vopay' | 'convertMyTemplate'>('vopay');
  const [fileUploadFormIsValid, setFileUploadFormIsValid] = useState(false);
  const [convertMyTemplateFormIsValid, setConvertMyTemplateFormIsValid] = useState(false);
  const { FileConversionEnabled } = useLockedFeatures();

  const {
    response: { Country, clientAccountsEnabled, AccountType, LoginUserName },
  } = useClientData();

  // File upload form state
  const [fileUploadForm, setFileUploadForm] = useState<FileUploadForm>({
    Service: 'Debit',
    Product: Country === 'US' ? 'ACH' : 'EFT',
    Currency: Country === 'US' ? 'USD' : 'CAD',
    ClientAccountsEnabled: clientAccountsEnabled,
    FileName: '',
    FileBase64: '',
  });

  // Upload states
  const [processedBulkFile, setProcessedBulkFile] = useState<
    {
      [key: string]: string | number | boolean | undefined;
    }[]
  >([]);

  // API hooks - we now have async methods
  const [, , sendBulkPaylink] = usePostBulkPaylink({});
  const [, , sendBulkRTRPaymentAsync] = usePostBulkRTRPayment({});
  const [, , sendBulkRTRCollection] = usePostBulkRTRCollection({});
  const [, , sendBulkPaymentAsync] = usePostBulkPayment({});
  const [, , sendBulkCollectionAsync] = usePostBulkCollection({});
  const [, , sendBulkBulkPayout] = usePostBulkBulkPayout({});
  const [, , sendBulkMoneyRequest] = usePostBulkMoneyRequest({});
  const [, , sendBulkPushToCard] = usePostBulkPushToCardPayment({});
  const [, , sendBulkCreditCard] = usePostBulkCreditCardCollection({});
  const [, , sendBulkACHCollection] = usePostBulkACHCollection({});
  const [, , sendBulkACHPayment] = usePostBulkACHPayment({});
  const [, , sendBulkRTP] = usePostBulkRTP({});
  const [, , sendBulkFedwireCollection] = useBulkFedwireCollection({});
  const [, , sendBulkFedwirePayment] = useBulkFedwirePayment({});
  const [, , sendBulkClientAccountTransfer] = usePostBulkClientAccountTransfer({});
  const [, , getAccountBalanceAsync] = usePostAccountBalance({});
  const { uploadFile } = useFileConversion();
  const [fileUploadValues, setFileUploadValues] = useState<ConvertMyTemplateFormValues>();
  const [, setDataFetchingInfo] = useDataFetchIndicator();

  // Scroll to top when review state changes to true
  useEffect(() => {
    if (isReview && contentRef.current) {
      contentRef.current.scrollTop = 0;
    }
  }, [isReview]);

  // File validation function
  const ValidateBulkFile = (
    SendingData: {
      [key: string]: string | number | boolean | undefined;
    }[],
    accountBalance: vp.AccountBalanceResponse,
    fileUploadForm: FileUploadForm,
  ) => {
    if (SendingData.length > 0) {
      const filteredSendingData = SendingData.map((data) => {
        const beneficiary = { ...data };
        const ext = fileUploadForm.FileName.split('.').pop()?.toLowerCase();
        if (ext === 'csv') {
          if (!beneficiary.PostalCode || beneficiary.PostalCode === '') {
            beneficiary.PostalCode = 'A1A1A1';
          }
          if (!beneficiary.City || beneficiary.City === '') {
            beneficiary.City = 'n/a';
          }
          if (!beneficiary.Province || beneficiary.Province === '') {
            beneficiary.Province = 'AB';
          }
          if (!beneficiary.Country || beneficiary.Country === '') {
            beneficiary.Country = 'CA';
          }
          if (!beneficiary.Address1 || beneficiary.Address1 === '') {
            beneficiary.Address1 = 'n/a';
          }
        }
        if (
          fileUploadForm.Product !== 'e-Transfer' &&
          (fileUploadForm.Product === 'ePay' ||
            fileUploadForm.Product === 'eCollect' ||
            fileUploadForm.Product === 'eFlex')
        ) {
          if (beneficiary.ScheduleStartDate == null || beneficiary.ScheduleStartDate === '') {
            beneficiary.ScheduleStartDate = format(new Date(), 'yyyy-MM-dd');
          } else {
            const date = parseISO(beneficiary.ScheduleStartDate as string);
            if (isBefore(date, new Date())) {
              beneficiary.ScheduleStartDate = format(new Date(), 'yyyy-MM-dd');
            }
          }
        }
        if (fileUploadForm.Product === 'EFT') {
          beneficiary.Country = parseCountry(beneficiary.Country as string);
          beneficiary.Province = parseProvince(beneficiary.Province as string, beneficiary.Country);
        }
        if (
          beneficiary.Document == null ||
          (beneficiary.Document as string).toLowerCase() === fileUploadForm.Service.toLowerCase()
        ) {
          return beneficiary;
        }

        return undefined;
      }).filter((x) => x !== undefined) as {
        [key: string]: string | number | boolean | undefined;
      }[];

      const balance = filteredSendingData.reduce((acc, obj) => {
        const amount = Number.parseFloat(obj?.Amount as string);
        if (!Number.isNaN(amount)) {
          return acc + amount;
        }
        return acc;
      }, 0);
      const diffCalc = Number.parseFloat(accountBalance.AvailableFunds) - balance;
      if (filteredSendingData.length !== SendingData.length) {
        alertDialog.error(tDialogue('IncorrectServiceError.title'), tDialogue('IncorrectServiceError.subTitle'));
      } else if (
        fileUploadForm.Service === 'Credit' &&
        diffCalc < -0.009 &&
        clientAccountsEnabled === false &&
        AccountType !== 'PaaS'
      ) {
        alertDialog.error(tDialogue('InsufficientFundsError.title'), tDialogue('InsufficientFundsError.subTitle'));
      } else {
        setProcessedBulkFile(SendingData);
        setIsReview(true);
      }
    } else {
      alertDialog.error(tDialogue('InvalidFileSize.title'), tDialogue('InvalidFileSize.subTitle'));
    }
  };

  // File processing function
  const ProcessBulkFile = async (
    accountBalance: vp.AccountBalanceResponse,
    fileUploadForm: FileUploadForm,
  ): Promise<void> => {
    const ext = fileUploadForm.FileName.split('.').pop()?.toLowerCase();
    const reader = new FileReader();

    if (ext === 'csv') {
      const wb = read(fileUploadForm.FileBase64, { type: 'base64', raw: true });
      const lines: string[][] = [];
      wb.SheetNames.forEach((sheetName) => {
        const csv = utils.sheet_to_csv(wb.Sheets[sheetName], { FS: ';', blankrows: false });
        if (csv.length) {
          const csvArr = csv.split('\n');
          lines.push(csvArr);
        } else {
          alertDialog.error(tDialogue('InvalidFileSize.title'), tDialogue('InvalidFileSize.subTitle'));
        }
      });

      const results = lines[0];
      const values = results[0].split(';');

      if (values[0] === 'Record_ID' || values[3] === 'Bank_Branch') {
        ValidateBulkFile(processFile(wb, fileUploadForm), accountBalance, fileUploadForm);
      } else {
        ValidateBulkFile(parseCSV(results, fileUploadForm), accountBalance, fileUploadForm);
      }
    } else if (ext === 'xlsx' || ext === 'xls' || ext === 'odf' || ext === 'csv' || ext === 'tsv') {
      const file = await (await fetch(fileUploadForm.FileBase64)).blob();

      reader.onload = function (e) {
        const data = e.target?.result as string;
        const wb = read(data, { type: 'binary' });
        ValidateBulkFile(processFile(wb, fileUploadForm), accountBalance, fileUploadForm);
      };

      reader.readAsBinaryString(file);
    } else if (ext === 'txt') {
      const file = await (await fetch(fileUploadForm.FileBase64)).blob();
      reader.onload = (event) => {
        const data = (event.target?.result as string) || '';
        const lines = data.split(/\r?\n/);
        const values = lines[0].split('\t');
        if (values[0] === 'Currency' || values[0] === 'CAD') {
          ValidateBulkFile(processMoneyWay(lines), accountBalance, fileUploadForm);
        } else if (lines[0].length === 1464) {
          ValidateBulkFile(processBankingFileStandard005(lines), accountBalance, fileUploadForm);
        } else if (
          lines[0].length === 80 ||
          lines[0].length === 27 ||
          lines[0].length === 26 ||
          lines[0].length === 28
        ) {
          ValidateBulkFile(processBankingFileBMO80(lines), accountBalance, fileUploadForm);
        } else {
          alertDialog.error(tDialogue('InvalidFile.title'), tDialogue('InvalidFile.subTitle'));
        }
      };

      reader.readAsText(file, 'ASCII');
    } else if (ext === 'deposits_out') {
      const file = await (await fetch(fileUploadForm.FileBase64)).blob();
      reader.onload = (event) => {
        const data = (event.target?.result as string) || '';
        const lines = data.split(/\r?\n/);
        if (lines[0].length === 1464) {
          ValidateBulkFile(processBankingFileStandard005(lines), accountBalance, fileUploadForm);
        } else {
          alertDialog.error(tDialogue('InvalidFile.title'), tDialogue('InvalidFile.subTitle'));
        }
      };

      reader.readAsText(file);
    } else {
      alertDialog.error(tDialogue('InvalidFile.title'), tDialogue('InvalidFile.subTitle'));
    }
  };

  // Send Bulk file Function
  const SendBulkFile = async (
    SendingData: {
      [key: string]: string | number | boolean | undefined;
    }[],
  ) => {
    if (SendingData.length === 0) {
      alertDialog.error(tDialogue('InvalidFileSize.title'), tDialogue('InvalidFileSize.subTitle'));
      return;
    }

    try {
      setDataFetchingInfo(true);

      const batchName = fileUploadForm.FileName;
      const batchData = JSON.stringify(SendingData).replace(/'/g, "\\\\'");
      const requestParams = {
        BatchName: batchName,
        BatchData: batchData,
      };

      let result: { Success: boolean; ErrorMessage?: string } = { Success: false, ErrorMessage: '' };

      if (fileUploadForm.Product === 'VopayInstant') {
        if (fileUploadForm.Service === 'Debit') {
          result = await sendBulkRTRCollection(requestParams);
        } else {
          result = await sendBulkRTRPaymentAsync(requestParams);
        }
      } else if (fileUploadForm.Product === 'EFT') {
        if (fileUploadForm.Service === 'Debit') {
          result = await sendBulkCollectionAsync(requestParams);
        } else {
          result = await sendBulkPaymentAsync(requestParams);
        }
      } else if (fileUploadForm.Product === 'e-Transfer') {
        if (fileUploadForm.Service === 'Debit') {
          result = await sendBulkMoneyRequest(requestParams);
        } else {
          result = await sendBulkBulkPayout(requestParams);
        }
      } else if (
        fileUploadForm.Product === 'ePay' ||
        fileUploadForm.Product === 'eCollect' ||
        fileUploadForm.Product === 'eFlex'
      ) {
        result = await sendBulkPaylink(requestParams);
      } else if (fileUploadForm.Product === 'OCT') {
        result = await sendBulkPushToCard(requestParams);
      } else if (fileUploadForm.Product === 'Credit Card') {
        result = await sendBulkCreditCard(requestParams);
      } else if (fileUploadForm.Product === 'ACH') {
        if (fileUploadForm.Service === 'Debit') {
          result = await sendBulkACHCollection(requestParams);
        } else {
          result = await sendBulkACHPayment(requestParams);
        }
      } else if (fileUploadForm.Product === 'RTP') {
        result = await sendBulkRTP(requestParams);
      } else if (fileUploadForm.Product === 'Fedwire') {
        if (fileUploadForm.Service === 'Debit') {
          result = await sendBulkFedwireCollection(requestParams);
        } else {
          result = await sendBulkFedwirePayment(requestParams);
        }
      } else if (fileUploadForm.Product === 'Client Account Transfer') {
        result = await sendBulkClientAccountTransfer(requestParams);
      } else {
        alertDialog.error(tDialogue('UploadError.title'), tDialogue('UploadError.unknownProductType'));
        return;
      }

      if (!result.Success && result.ErrorMessage !== '') {
        alertDialog.error(tDialogue('UploadError.title'), result.ErrorMessage);
        return;
      }

      // Success - calculate total amount for query params
      const totalAmount = processedBulkFile.reduce((acc, obj) => {
        const amount = Number.parseFloat(obj?.Amount as string);
        if (!Number.isNaN(amount)) {
          return acc + amount;
        }
        return acc;
      }, 0);

      // Handle currency - use form currency or default based on account country
      const displayCurrency = fileUploadForm.Currency || (Country === 'US' ? 'USD' : 'CAD');

      // Create query params for confirmation page
      const queryParams = new URLSearchParams({
        fileName: fileUploadForm.FileName,
        product: fileUploadForm.Product,
        service: fileUploadForm.Service,
        currency: displayCurrency,
        numberOfTransactions: processedBulkFile.length.toString(),
        amount: totalAmount.toString(),
      });

      // Reset form and state
      setFileUploadForm({
        ...fileUploadForm,
        FileName: '',
        FileBase64: '',
      });
      setIsReview(false);

      // Redirect to confirmation page with query params
      history.push(`/file-upload/confirm?${queryParams.toString()}`);
      onClose();
    } catch (error) {
      console.error('Bulk file upload error:', error);
      alertDialog.error(tDialogue('UploadError.title'), tDialogue('UploadError.unexpectedError'));
    } finally {
      setDataFetchingInfo(false);
    }
  };

  // Handlers
  const handlers = {
    onFileUpload: async (values: FileUploadForm) => {
      const upload = { ...values };
      setFileUploadForm(upload);

      try {
        // Use async method to get account balance
        const accountBalanceResponse = await getAccountBalanceAsync({
          Currency: upload.Currency,
          PortalUser: LoginUserName,
        });
        // Process file after getting balance
        if (accountBalanceResponse.Success) {
          await ProcessBulkFile(accountBalanceResponse, upload);
        }
      } catch (error) {
        console.error('Error getting account balance:', error);
        alertDialog.error(tDialogue('UploadError.title'), tDialogue('UploadError.accountBalanceFailed'));
      }
    },
    onFileSubmit: async () => {
      await SendBulkFile(processedBulkFile);
    },
    onFileConversionUpload: (values: ConvertMyTemplateFormValues) => {
      setIsReview(true);
      setFileUploadValues(values);
    },
    onUpload: async () => {
      if (fileUploadValues && fileUploadValues.FileBase64 && fileUploadValues.Service) {
        try {
          // Call the file conversion API
          setDataFetchingInfo(true);
          const conversionResult = await uploadFile({
            SourceFileName: fileUploadValues.FileName,
            SourceFile: fileUploadValues.FileBase64,
            Service: fileUploadValues.Service,
          });

          const queryParams = new URLSearchParams({
            transferType: fileUploadValues.Service,
            fileName: fileUploadValues.FileName,
            uploadFile: fileUploadValues.FileName,
            numberOfTransactions: conversionResult.TransactionCount.toString(),
            totalAmount: conversionResult.TotalAmount,
            fileConversionID: conversionResult.FileConversionID,
            service: fileUploadValues.Service,
          });
          setIsReview(false);
          setFileUploadValues(undefined);

          history.push(`/file-upload/convert-template/confirm?${queryParams.toString()}`);
        } catch (err) {
          const msg = err instanceof Error ? err.message : tDialogue('ConversionError.subTitle');
          alertDialog.error(tDialogue('ConversionError.title'), msg);
        } finally {
          handleClose();
          setDataFetchingInfo(false);
        }
      }
    },
  };

  // Custom onClose handler to reset review state
  const handleClose = () => {
    setIsReview(false);
    onClose();
  };

  return (
    <>
      <SideDrawer
        isOpen={isOpen}
        onClose={isReview && processingOption !== 'convertMyTemplate' ? undefined : handleClose}
        title={
          isReview ? (
            <Box base="8" flx="col" gap="md">
              {isReview && processingOption !== 'convertMyTemplate' && (
                <UtilButton iconLeft="chevron_left" size="large" onClick={() => setIsReview(false)}>
                  {tGlobal('Buttons.back')}
                </UtilButton>
              )}
              <span>{t('reviewTitle', { defaultValue: 'Review' })}</span>
            </Box>
          ) : (
            t('title', { defaultValue: 'Upload File' })
          )
        }
        style={{
          width: isReview && processingOption !== 'convertMyTemplate' ? '100%' : undefined,
        }}
        footer={
          <>
            {!isReview && (
              <Box flx="col">
                <Button
                  disabled={
                    processingOption === 'convertMyTemplate' ? !convertMyTemplateFormIsValid : !fileUploadFormIsValid
                  }
                  colorScheme="primary"
                  form="fileUploadForm"
                  type="submit"
                >
                  {tGlobal('Buttons.review')}
                </Button>
              </Box>
            )}
            {isReview && processingOption === 'vopay' && (
              <Box jc="fe" gap="md" width="100%">
                <Button colorScheme="primary" type="button" onClick={handlers.onFileSubmit}>
                  {tGlobal('Buttons.submit')}
                </Button>
              </Box>
            )}
            {isReview && processingOption === 'convertMyTemplate' && (
              <Box jc="fe" flx="col" gap="md" width="100%">
                <Button colorScheme="primary" type="button" onClick={handlers.onUpload}>
                  {tGlobal('Buttons.submit')}
                </Button>
              </Box>
            )}
          </>
        }
      >
        <div
          ref={contentRef}
          style={{
            height: '100%',
          }}
        >
          {(!isReview || processingOption === 'convertMyTemplate') && (
            <Box flx="col" gap="md" base="8">
              {/* This is temporary until we figure out how it is going to work and we can have it locked */}
              {FileConversionEnabled && (
                <Card>
                  <Box flx="col" gap="ml" base="8">
                    <Title level="5">{t('processingOptionsTitle', { defaultValue: 'Processing Options' })}</Title>
                    <Box base="8" flx="col" gap="md">
                      <RadioCard
                        value="vopay"
                        name="processingOption"
                        checked={processingOption === 'vopay'}
                        disabled={isReview}
                        onChange={(value) => setProcessingOption(value as 'vopay' | 'convertMyTemplate')}
                      >
                        <Box base="8" flx="col" gap="sm">
                          <BodyText weight="em">
                            {t('directProcessingTitle', { defaultValue: 'Direct Transaction Processing' })}
                          </BodyText>
                          <BodyText>
                            {t('directProcessingDescription', {
                              defaultValue:
                                'Use a pre-configured template to process transactions through the integrated payment network.',
                            })}
                          </BodyText>
                        </Box>
                      </RadioCard>
                      <RadioCard
                        value="convertMyTemplate"
                        name="processingOption"
                        checked={processingOption === 'convertMyTemplate'}
                        disabled={!FileConversionEnabled || isReview}
                        onChange={(value) => setProcessingOption(value as 'vopay' | 'convertMyTemplate')}
                        customIndicator={
                          FileConversionEnabled ? undefined : (
                            <Button
                              iconRight="lock"
                              size="smallest"
                              colorScheme="primary"
                              onClick={() => setShowUpgradePlanModal(true)}
                            >
                              {t('unlockButton', { defaultValue: 'Unlock' })}
                            </Button>
                          )
                        }
                      >
                        <Box base="8" flx="col" gap="sm">
                          <BodyText weight="em">
                            {t('convertTemplateTitle', { defaultValue: 'Convert my Template' })}
                          </BodyText>
                          <BodyText>
                            {t('convertTemplateDescription', {
                              defaultValue: 'Upload your own template & convert it to any bank format',
                            })}
                          </BodyText>
                        </Box>
                      </RadioCard>
                    </Box>
                  </Box>
                </Card>
              )}
              {processingOption === 'vopay' && (
                <FileUploadFormDisplay
                  defaultValues={fileUploadForm}
                  upload={handlers.onFileUpload}
                  onValidate={(value) => setFileUploadFormIsValid(value)}
                />
              )}
              {processingOption === 'convertMyTemplate' && (
                <ConvertMyTemplateFormValues
                  onSubmit={handlers.onFileConversionUpload}
                  onValidate={(value) => setConvertMyTemplateFormIsValid(value)}
                  isReviewing={isReview}
                />
              )}
            </Box>
          )}

          {isReview && processingOption === 'vopay' && (
            <Box flx="col" gap="lg">
              <FileUploadReview
                product={fileUploadForm.Product}
                service={fileUploadForm.Service}
                fileName={fileUploadForm.FileName}
                numberOfTransactions={processedBulkFile.length}
                amount={processedBulkFile.reduce((acc, obj) => {
                  const amount = Number.parseFloat(obj?.Amount as string);
                  if (!Number.isNaN(amount)) {
                    return acc + amount;
                  }
                  return acc;
                }, 0)}
                currency={fileUploadForm.Currency}
                clientAccountsEnabled={clientAccountsEnabled}
                transactionData={processedBulkFile}
              />
            </Box>
          )}
        </div>
      </SideDrawer>

      <ConvertTemplateUpgrade
        isVisible={showUpgradePlanModal}
        onCloseModal={() => {
          setShowUpgradePlanModal(false);
        }}
      />
    </>
  );
};

export default FileUploadDrawer;
